import { defineMessages } from 'react-intl';

import { RadioGroup } from 'atoms/inputs/RadioGroup';
import { CreditCard } from 'components/buyActions/CreditCard';
import { EthWallet } from 'components/paymentMethods/EthWallet';
import { FiatWallet } from 'components/paymentMethods/FiatWallet';
import { useCurrentUserContext } from 'contexts/currentUser';
import { usePreferredPaymentMethod } from 'hooks/wallets/usePreferredPaymentMethod';
import {
  PaymentMethod,
  PaymentProvider_paymentMethod,
} from 'lib/paymentMethod';

import { useCurrentUserPaymentMethods } from '../PaymentMethods/useCurrentUserPaymentMethods';
import { SettingsSection } from '../SettingsSection';

const messages = defineMessages({
  title: {
    id: 'Settings.preferredPaymentMethods.title',
    defaultMessage: 'Choose your preferred payment method',
  },
});

export const PreferredPaymentMethods = () => {
  const {
    walletPreferences: { showEthWallet, showFiatWallet },
  } = useCurrentUserContext();
  const creditCards = useCurrentUserPaymentMethods('cache-only');
  const { preferredOrderedPaymentMethod, updatePreferredPaymentMethod } =
    usePreferredPaymentMethod();

  const paymentMethods: Record<
    string,
    | PaymentProvider_paymentMethod
    | PaymentMethod.ETH_WALLET
    | PaymentMethod.FIAT_WALLET
  > = {
    [PaymentMethod.ETH_WALLET]: PaymentMethod.ETH_WALLET,
    [PaymentMethod.FIAT_WALLET]: PaymentMethod.FIAT_WALLET,
    ...creditCards?.reduce<Record<string, PaymentProvider_paymentMethod>>(
      (acc, cc) => ({
        ...acc,
        [`credit_card_${cc.id}`]: cc,
      }),
      {}
    ),
  };

  const options = [
    ...(showEthWallet
      ? [
          {
            label: <EthWallet withoutBalance />,
            value: PaymentMethod.ETH_WALLET,
          },
        ]
      : []),
    ...(showFiatWallet
      ? [
          {
            label: <FiatWallet withoutBalance />,
            value: PaymentMethod.FIAT_WALLET,
          },
        ]
      : []),
    ...creditCards
      ?.filter(pm => !pm.disabled)
      .map(creditCard => ({
        label: (
          <CreditCard
            creditCard={creditCard.card}
            color={creditCard.disabled ? 'var(--c-nd-600)' : undefined}
          />
        ),
        value: `credit_card_${creditCard.id}`,
      })),
  ];

  return (
    <SettingsSection title={messages.title}>
      <RadioGroup
        preventPreselection
        value={preferredOrderedPaymentMethod || undefined}
        withSpacing
        name="preferred-currency"
        options={options}
        onChange={(e: string) => {
          updatePreferredPaymentMethod(paymentMethods[e]);
        }}
      />
    </SettingsSection>
  );
};
